/**
 * RouteGuard: declarative route-access gate for the PWA shell (T010).
 *
 * Consumes the public `useSession()` hook (NOT `PortsContext` directly) so
 * adapter swapping at the composition root propagates here without any
 * code change in the guard. Sits between the route registry (T019
 * `RouteRegistry`) and the route component itself, wrapping every route
 * whose descriptor in `shell/routes.ts` carries `protected: true`
 * (optionally with a `role` narrowing). The registry decides which routes
 * receive this wrapper; the guard itself does NOT know about route paths.
 *
 * ## Semantics
 *
 * The guard implements precedence-ordered checks against the active
 * `AuthState` discriminated union from `./SessionPort.ts`:
 *
 *   0. **Loading → wait (render a placeholder, NEVER redirect).** When
 *      `state.kind === 'loading'` the active provider (the dual-audience
 *      composite, whose BFF source `RealSessionProvider` learns identity from
 *      an async `session.me` round-trip) has not yet resolved whether the
 *      visitor is anonymous or admin. The guard MUST wait: it renders a
 *      minimal `role="status"` placeholder and makes NO redirect decision until
 *      the session resolves. This is the load-bearing fix for the admin-cutover
 *      redirect bug, without it, the guard would read the initial pre-resolution
 *      state as "not signed in" and bounce an already-authenticated admin to
 *      `/login` (and break every hard-refresh of a protected route for an
 *      admin) BEFORE `session.me` resolved to admin. Checked FIRST, ahead of
 *      every anonymous branch below. Synchronous providers (the demo adapter,
 *      snapshot fixtures) never emit `loading`, so this branch is inert for
 *      them and the historical behaviour is unchanged.
 *
 *   1. **Anonymous → the login wall (D-130, the corrected proto-faithful
 *      model).** A logged-out visitor is redirected to `/login`; member
 *      surfaces are NOT anonymously browsable. The one route-specific carve-out
 *      is checked first so it wins for admin routes:
 *        - `role="admin"` WITH `adminSignInUrl` wired → the backend admin
 *          sign-in with the current path preserved as `returnTo`.
 *        - every other case (admin without the prop, `role="member"`, no role)
 *          → the `/login` wall.
 *      This is the D-130 login wall, reversing the earlier items-2+3 "browsable
 *      non-member" model. Both arms use a FULL-PAGE redirect (`FullPageRedirect`,
 *      `window.location.replace`), NOT a client-side `<Navigate>`: `/login` is the
 *      backend member Telia-start (`GET /login`, a SERVER route parallel to
 *      `/admin/login`, 032 US1), so a react-router `<Navigate to="/login">` would
 *      render the SPA route (the retiring demo form, T035) and never start the
 *      Telia flow — the full-page nav reaches the proxied api so a session can be
 *      minted. The redirect never loops: the api serves `/login` (it starts
 *      Telia), it does not re-enter this guard. Membership gating (precedence 2-3
 *      below) applies only to AUTHENTICATED sessions.
 *
 *   2. **Member on a `role="admin"` route → `/forbidden`.** The target
 *      path is where `AdminForbidden` (025-admin-area) is mounted; that
 *      route is itself guarded as `protected: true` with no `role`, so a
 *      member who bounces there can render the forbidden surface without
 *      triggering a second redirect. This is the cleanest react-router
 *      path: we bounce the user to a URL that *exists in the registry*
 *      and let the registry render the forbidden screen. The target is
 *      `/forbidden`, NOT `/admin`: `/admin` is now the admin launch
 *      portal (`role: 'admin'`, 025-admin-area US1), so bouncing a member
 *      there would loop (`/admin` → guard → `/admin` → …) — a dedicated
 *      non-admin-gated forbidden surface is the bounce target instead. No
 *      separate `/admin/denied` convention is introduced.
 *
 *   3. **Otherwise → render `children`.** Admin state satisfies any
 *      role requirement (admin ≥ member); member state satisfies
 *      `undefined` and `role="member"`; an authenticated user without a
 *      role narrowing passes straight through.
 *
 * ## Return-location preservation (wired at the cutover, T025/T045/D-106)
 *
 * When the `adminSignInUrl` prop is supplied AND the visitor is anonymous
 * on a `role="admin"` route, the guard full-page redirects to the backend
 * admin sign-in carrying the current path as an encoded `returnTo`
 * (`${adminSignInUrl}?returnTo=${encodeURIComponent(currentPath)}`), so the
 * post-login callback returns the browser to where it was headed.
 * `RouteRegistry` now passes `adminSignInUrl="/admin/login"` (T045), so this
 * branch is LIVE for the anonymous-on-`role="admin"` case. An anonymous visitor
 * on an admin route WITHOUT the prop falls through to the `/login` wall;
 * member/public routes follow that same D-130 login wall (anonymous → `/login`),
 * carrying their own `returnTo` the same way. It is safe to wire because the live
 * `ports.session` is the single BFF `RealSessionProvider` (RD-111) that can read
 * the minted `__Secure-oma_session` cookie. Under the old demo-only provider this
 * redirect would have looped (the demo provider cannot read the cookie),
 * which is why the prop stayed dormant until this cutover.
 *
 * The guard does NOT reimplement the server's `returnTo` allowlist
 * client-side. The current path comes from react-router's `useLocation()`
 * (`pathname + search`), a trustworthy same-origin path; the SERVER's
 * `returnTo.ts` validates the `returnTo` on the callback return (shipped in
 * US1). A client-side allowlist copy would be a no-shadow-copy / drift
 * violation (P7). The client's only responsibility is to `encodeURIComponent`
 * the current path.
 *
 * Both `/admin/login` AND the member Telia-start `/login` are SERVER routes, not
 * react-router routes, so the guard cannot use `<Navigate>` for either. It uses a
 * full-page `window.location.replace` inside an effect (`FullPageRedirect` below),
 * which renders an accessible `role="status"` placeholder while the browser tears
 * down the page. (`<Navigate>` survives only for the AUTHENTICATED bounces below —
 * member-on-admin → `/forbidden` and the capability bounce → `/` — which ARE
 * react-router routes.)
 *
 * ## Adapter boundary
 *
 * The guard consumes `useSession()` and is indifferent to which adapter
 * backs it. Post-cutover (032 / RD-111) the live `ports.session` is the
 * single BFF `RealSessionProvider` serving admin | member | anonymous from one
 * `session.me`; the guard's `state.kind`-based branches are unchanged by that.
 * The only
 * cutover-coupled change here is the `adminSignInUrl` prop now being passed
 * by `RouteRegistry` (T045), which activates the anonymous-admin returnTo
 * redirect documented above.
 *
 * ## Architecture
 *
 * Uses `<Navigate>` from `react-router` (v7.14.2, already installed and used in
 * `main.tsx`) for the AUTHENTICATED in-SPA bounces (member-on-admin →
 * `/forbidden`, capability → `/`) with `replace` enabled so the guarded page does
 * NOT enter the browser history. The ANONYMOUS sign-in walls (member/public AND
 * admin → the backend `/login` / `/admin/login` SERVER routes) instead use the
 * full-page `FullPageRedirect`, because those targets are outside react-router's
 * table and must hit the api to start the real sign-in flow (see §Semantics
 * item 1 and §"Return-location preservation").
 */

import { withoutEntryAttributionQueryParameters } from '@omaplus/shared-analytics-attribution';
import { type JSX, type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslate } from '../i18n/useTranslate.js';
import type { Capability } from '../membership/capabilities.js';
import { isCapabilityAllowed } from '../membership/capabilities.js';
import { useMembershipStatus } from '../membership/useMembershipStatus.js';
import { SessionStatusBoundary } from './SessionStatusBoundary.js';
import { useSession } from './useSession.js';

export interface RouteGuardProps {
  /**
   * Optional role narrowing.
   *
   * - `undefined`: any authenticated user passes (member or admin).
   * - `'member'`: member or admin passes; an anonymous visitor is walled to
   *   `/login` (D-130, the restored login wall), like every other route.
   * - `'admin'`: only admin passes; member redirects to `/forbidden`
   *   (the `AdminForbidden` surface from 025-admin-area); an anonymous visitor
   *   hits the RETAINED admin wall — the backend admin sign-in with `returnTo`
   *   when `adminSignInUrl` is set, else the `/login` fallback (see below).
   */
  // `?: T | undefined` under `exactOptionalPropertyTypes`: an explicit
  // `undefined` is a documented, meaningful value here ("any authenticated
  // user passes"), and callers forward a `Role | undefined` prop directly.
  readonly role?: 'member' | 'admin' | undefined;
  /**
   * Backend admin sign-in entry URL (a SERVER route, e.g. `/admin/login`).
   * When supplied AND the visitor is anonymous on a `role="admin"` route, the
   * guard full-page redirects there carrying the current path as an encoded
   * `returnTo`. Post-cutover (T045 / D-106) `RouteRegistry` wires this to
   * `"/admin/login"`, so the redirect is LIVE for the anonymous-on-admin
   * case. The prop stays optional: when omitted (e.g. a unit test that mounts
   * `RouteGuard` directly) the returnTo branch is inert and an anonymous visitor
   * on an admin route falls through to the `/login` wall; member/public routes
   * follow the same D-130 login wall (anonymous → `/login`). See the file
   * header §"Return-location preservation".
   */
  // `?: T | undefined` under `exactOptionalPropertyTypes`: an explicit
  // `undefined` means "no admin returnTo redirect" (the inert branch); the
  // gated branch tests `adminSignInUrl !== undefined`.
  readonly adminSignInUrl?: string | undefined;
  /**
   * Optional membership-capability requirement (028-membership-state-gating).
   * When set AND the visitor is a MEMBER whose membership state does not allow
   * the capability, the guard bounces to `/` (Home), where the non-member
   * notice and the greyed surface render. Enforcement is here, not cosmetic
   * greying, so a greyed action is refused even on direct-route access
   * (research Decision 9 / SC-002 / SC-003). Admin sessions are exempt (admin
   * elevation carries no membership context, spec 023 FR-023). Member-record
   * detail stays in the membership layer; the guard only reads the verdict.
   */
  readonly requiredCapability?: Capability | undefined;
  readonly children: ReactNode;
}

function memberLoginTarget(returnTo: string): string {
  return `/login?${new URLSearchParams({ returnTo }).toString()}`;
}

function sanitizedReturnTo(pathname: string, search: string, hash: string): string {
  return `${pathname}${withoutEntryAttributionQueryParameters(search)}${hash}`;
}

export function RouteGuard({
  role,
  adminSignInUrl,
  requiredCapability,
  children,
}: RouteGuardProps): JSX.Element {
  const { state } = useSession();
  // Read unconditionally (Rules of Hooks, R16); used only in the member
  // capability branch below.
  const membershipStatus = useMembershipStatus();
  // `useLocation()` is the trustworthy same-origin source for the current
  // path. Read unconditionally (Hooks rules, R40); the value is only used
  // in the anonymous-admin returnTo branch below.
  const location = useLocation();

  // Loading branch (precedence 0, checked FIRST): the BFF-backed session has
  // not yet resolved (`RealSessionProvider` learns identity from an async
  // `session.me` round-trip, so a fresh SPA load / hard-refresh starts here).
  // The guard WAITS, rendering a minimal `role="status"` placeholder, and
  // makes NO redirect decision until resolution. This MUST sit before every
  // anonymous branch below: treating `loading` as anonymous would bounce an
  // already-authenticated admin to `/login` before `session.me` resolves (the
  // admin-cutover redirect bug). Synchronous providers never emit `loading`,
  // so this is inert for them.
  if (state.kind === 'signingOut' || state.kind === 'signOutFailed') {
    return <SessionStatusBoundary>{null}</SessionStatusBoundary>;
  }
  if (state.kind === 'loading') {
    return <SessionLoadingPlaceholder />;
  }

  // Anonymous-on-admin returnTo branch (LIVE when `adminSignInUrl` is
  // supplied, which `RouteRegistry` now does, T045/D-106; inert when a
  // caller omits the prop): an anonymous visitor on an admin-only route is
  // sent to the backend admin sign-in with the current path preserved as
  // `returnTo`. Checked BEFORE the general login wall below so the
  // returnTo-carrying redirect wins for an admin route when the prop is
  // present; absent it, the condition is false and the visitor falls through to
  // the `/login` wall (same `/login` destination, just without a `returnTo`).
  // `/admin/login` is a SERVER route, so this uses a full-page redirect, not
  // `<Navigate>`. The shared attribution helper removes entry evidence before
  // the redirect; the server's `returnTo.ts` allowlist validates the value on
  // the callback return (no client-side allowlist copy, P7).
  if (state.kind === 'anonymous' && role === 'admin' && adminSignInUrl !== undefined) {
    const currentPath = sanitizedReturnTo(location.pathname, location.search, location.hash);
    const target = `${adminSignInUrl}?returnTo=${encodeURIComponent(currentPath)}`;
    return <FullPageRedirect to={target} />;
  }

  // Login wall RESTORED (D-130, the corrected proto-faithful model). After the
  // admin returnTo branch above, ANY remaining anonymous (logged-out) visitor
  // is redirected to `/login`: member surfaces are not anonymously browsable,
  // so a logged-out visitor meets the sign-in entry first. This single wall
  // covers member, public, AND admin-without-`adminSignInUrl` routes (all land
  // on `/login`), and reverses the earlier items-2+3 "browsable non-member"
  // model. Membership gating (the branches below) applies only to AUTHENTICATED
  // sessions — an anonymous visitor never reaches them.
  //
  // FULL-PAGE redirect, NOT `<Navigate>` (032 US1): `/login` is the backend
  // member Telia-start (`GET /login`, a SERVER route parallel to `/admin/login`),
  // not the SPA's react-router `/login`. A client-side `<Navigate to="/login">`
  // would render the SPA route (the retiring demo form, T035) and never reach
  // the api, so no Telia flow ever starts and no session is minted. Reusing the
  // SAME `FullPageRedirect` mechanism the admin branch uses above sends the
  // browser to the proxied api Telia-start (`/login` is proxied in
  // `vite.config.ts`), carrying the current path as an encoded `returnTo` so the
  // post-login callback returns the browser where it was headed. The wall itself
  // (anonymous → `/login`) is unchanged; only the mechanism moves from a
  // client-side nav to a full-page one. The redirect never loops: the api
  // serves `/login` (it starts Telia), it is not a SPA route that re-enters this
  // guard.
  if (state.kind === 'anonymous') {
    const currentPath = sanitizedReturnTo(location.pathname, location.search, location.hash);
    return <FullPageRedirect to={memberLoginTarget(currentPath)} />;
  }

  // Role enforcement. Admin satisfies every role requirement (admin ≥
  // member), so the only failing authenticated case is member-on-admin.
  // Target `/forbidden` is the AdminForbidden (025-admin-area) surface;
  // that route is `protected: true` with no `role`, so this bounce lands
  // cleanly and does not trigger a second redirect. The target is
  // `/forbidden`, NOT `/admin`: `/admin` is now the admin launch portal
  // (`role: 'admin'`), so bouncing a member to `/admin` would loop
  // (`/admin` → guard → `/admin` → …) — the bounce points at a
  // non-admin-gated surface instead.
  if (role === 'admin' && state.kind === 'member') {
    return <Navigate to="/forbidden" replace />;
  }

  // Membership-capability enforcement (042-membership-access-binary, D-202,
  // superseding 028). A MEMBER whose membership state does not allow the route's
  // required capability is bounced to `/` (Home). Under binary access a live
  // member session only ever carries `'valid'` (an ended membership is refused a
  // session at sign-in and never reaches the shell), so for a real member every
  // capability is allowed and this bounce is the fail-closed backstop for the
  // degenerate/indeterminate case (a member session missing the field → `'none'`,
  // the 042 contract's fail-closed floor), via `useMembershipStatus`. It remains the direct-route
  // enforcement behind the cosmetic greying (SC-002 / SC-003). Anonymous visitors
  // were already walled to `/login` above (D-130), so only an authenticated
  // MEMBER reaches here. Admin is exempt: it carries no membership context (spec
  // 023 FR-023) and `state.kind` is `'admin'`, not `'member'`, here.
  if (
    requiredCapability !== undefined &&
    state.kind === 'member' &&
    !isCapabilityAllowed(membershipStatus, requiredCapability)
  ) {
    return <Navigate to="/" replace />;
  }

  // Authenticated + role satisfied. Render the wrapped subtree.
  // `<>…</>` would be equally correct; returning `children` directly
  // would force the return type away from `React.JSX.Element`. Wrapping
  // in a fragment preserves the prop signature without introducing a
  // DOM node.
  return <>{children}</>;
}

/**
 * Transient placeholder rendered while the active session is still resolving
 * (`AuthState.kind === 'loading'`). PURE (no side effect, unlike
 * `FullPageRedirect`): the guard is simply waiting for the BFF `session.me`
 * round-trip to settle, after which a re-render lands on a real branch
 * (anonymous → redirect, or authenticated → children). The wait is transient by
 * construction: `RealSessionProvider.refresh()` resolves `loading` to
 * anonymous|admin on success AND on a query rejection, so the guard never
 * parks here indefinitely.
 *
 * Renders an accessible `role="status" aria-live="polite"` live region (the
 * same minimal shape as `FullPageRedirect`) so a screen-reader user is told the
 * sign-in check is underway rather than hearing silence. Copy is Finnish-first
 * via `useTranslate` (FR-040); `shell.session.loading` is route-neutral because
 * the guard wraps any protected route and must not assume the admin audience.
 */
function SessionLoadingPlaceholder(): JSX.Element {
  const t = useTranslate();
  return (
    <p role="status" aria-live="polite">
      {t('shell.session.loading')}
    </p>
  );
}

/**
 * Full-page redirect to a SERVER route (one outside react-router's table —
 * the backend admin sign-in `/admin/login`, OR the backend member Telia-start
 * `/login`, 032 US1). The navigation is a side effect, so it
 * lives in `useEffect`, NEVER inline in render (render must stay pure, R2; a
 * bare `window.location.replace` in the component body would fire on every
 * render pass). `useEffect` itself still double-invokes under React StrictMode
 * in development (mount, unmount, mount), so the replacement runs twice there, but
 * navigating to the SAME `to` twice is idempotent (the browser is already
 * tearing the page down to that URL), so the double-fire is harmless. The
 * point of the effect is render purity, not avoiding the StrictMode replay.
 *
 * Renders an accessible `role="status"` live region as a transient
 * placeholder while the browser tears down the page, so a screen-reader
 * user is told a navigation is underway rather than hearing silence. The
 * copy is intentionally route-neutral and minimal; the page is about to be
 * replaced wholesale by the server response.
 *
 * `window.location.replace` (not `.href =`) keeps the call mockable in tests
 * (`vi.spyOn(window.location, 'replace')`) and reads as a navigation at the
 * call site. The effect depends on `to` so a changed target re-navigates;
 * in practice `to` is stable for a given guard decision.
 */
function FullPageRedirect({ to }: { readonly to: string }): JSX.Element {
  const t = useTranslate();

  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  // Finnish-first copy via `useTranslate` (FR-040), the guard always
  // renders inside the provider tree (RouteRegistry sits under
  // PortsProvider), so the i18n provider is present here. Both callers (the
  // anonymous-on-admin branch and the anonymous member/public login wall, 032
  // US1) are heading to a sign-in entry, so the route-neutral
  // `admin.login.redirecting` copy ("Siirrytään kirjautumiseen…") is honest for
  // both — its text does not name the admin audience, so no new key is needed.
  // Tests assert the `role="status"` presence.
  return (
    <p role="status" aria-live="polite">
      {t('admin.login.redirecting')}
    </p>
  );
}
